import { usePinInput } from '@/components/elements/forms/PinInput/PinInput';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import CardWrapper from '@/components/modules/CardWrapper';

import Button, { ButtonProps } from '@/components/elements/forms/buttons/Button/Button';
import ListInput from '@/components/elements/forms/listInput/ListInput/ListInput';
import Header from '@/components/elements/Header/Header';
import { HeaderButtonIdentifier } from '@/components/elements/HeaderButton/HeaderButton.types';
import BundleOtherLocationsListItem from '@/components/elements/lists/BundleOtherLocationsListItem/BundleOtherLocationsListItem';
import Icon from '@/components/icons/Icon';
import CheckoutViewLayout from '@/components/layouts/CheckoutViewLayout';
import { ThreeDSComponent } from '@/components/modules/adyen';
import BundleNoticeBanner from '@/components/modules/BundleNoticeBanner';
import CheckoutDetails from '@/components/modules/checkout/CheckoutDetails/CheckoutDetails';
import CheckoutModal from '@/components/modules/checkout/CheckoutModal/CheckoutModal';
import {
  CheckoutModalProvider,
  useCheckoutModal,
} from '@/components/modules/checkout/CheckoutModal/CheckoutModal.hooks';
import CheckoutTerms from '@/components/modules/checkout/CheckoutTerms/CheckoutTerms';
import KlarnaOptionsModal from '@/components/modules/checkout/KlarnaOptionsModal';
import PromotedPaymentMethods from '@/components/modules/checkout/PromotedPaymentMethods/PromotedPaymentMethods';
import WalleyPhoneModal from '@/components/modules/checkout/WalleyPhoneModal';
import CreditCardModal from '@/components/modules/modals/CreditCardModal';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import SwishModal from '@/components/modules/modals/SwishModal';
import GoBack from '@/components/modules/pages/bokningar/GoBack';
import FloatingCTAWrapper from '@/components/modules/pages/booking/checkout/FloatingCTAWrapper';
import SEO, { seoPropsFromBaseString } from '@/components/modules/SEO';
import { TooltipPortal } from '@/components/modules/Tooltip';
import UpdateUserInfo from '@/components/modules/UpdateUserInfo/UpdateUserInfo';
import useUpdateUserInfo from '@/components/modules/UpdateUserInfo/UpdateUserInfo.hooks';
import UserInfo from '@/components/templates/checkout/UserInfo/UserInfo';
import UserLogin from '@/components/templates/checkout/UserLogin/UserLogin';
import VerifyOTP from '@/components/templates/login/VerifyOTP';
import VerifySuccess from '@/components/templates/login/VerifySuccess';
import { SCREEN_NAME } from '@/constants/analytics';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { TermLinkIdentifier } from '@/constants/terms';
import { AdyenCheckoutComponent, useOnCompleteAdyenCheckout } from '@/features/payments';
import {
  usePaymentMethodsContext,
  UsePaymentMethodsProvider,
  WithPaymentMethodsLoaded,
} from '@/features/payments/hooks/usePaymentMethods';
import { KlarnaVariant } from '@/features/payments/types/adyen.types';
import { getValidKlarnaBrands } from '@/features/payments/utils/paymentConfig.utils';
import { classnames, getUserInitials, isSistaminuten, url } from '@/helpers';
import { loggedUser, validatePhoneNumber } from '@/helpers/userHelper';
import { getThreeDSecureRedirectResultIfAny } from '@/helpers/adyen';
import { getScreenNameFromPath, trackMpEvent } from '@/helpers/analyticsTS';
import { getOtherBundleLocations } from '@/helpers/bundle';
import {
  getBundleSubmitLabel,
  getCheckoutSubmitIconVariantIfAny,
  getCheckoutTerms,
  mapBundleStateToCheckoutDetailsProps,
} from '@/helpers/checkout';
import { buyBundleFlowTrackingSession, getSummaryProperties } from '@/helpers/checkoutFlowTrackingSession';
import { loginTrackingSession } from '@/helpers/loginTrackingSession';
import { PaymentMethodCheckout, trackPaymentMethodChange } from '@/helpers/paymentMethodTracking';
import { isNationalId } from '@/helpers/utilsTS';
import withBundleCheckoutFallback from '@/hoc/withBundleCheckoutFallback';
import withWalletPayment from '@/hoc/withWalletPayment';
import { useAppSelector } from '@/hooks';
import { useCardsManager, UseCardsProvider } from '@/hooks/adyen/useCards';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { LoginContext } from '@/hooks/useLogin';
import useMobileView from '@/hooks/useMobileView';
import { usePaymentsHistory } from '@/hooks/usePaymentsHistory';
import { _s } from '@/locale';
import { _t } from '@/locale/i18n';
import { saveCofCheckoutStateToLocalStorage } from '@/pages/validate-cof-payment-redirect/ValidateCofPaymentRedirect.hooks';
import { CheckoutMissingAction, SelectedPaymentMethod, selectedPaymentMethodSchema } from '@/types/checkout';
import { PaymentCard } from '@/types/paymentcards';
import { bundleStateSchema } from '@/types/state/bundle';
import * as Sentry from '@sentry/react';
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BundleCheckoutProvider, FormDataProvider, useBundleCheckout, useFormData } from './BundleCheckout.hooks';

export const baseTranslationKey = 'pages.bundles.checkout.BundleCheckout';

const getBundleSummaryFinalPrice = (summary: { availablePaymentMethods?: { final?: number }[] } | null): number => {
  const fallback = summary?.availablePaymentMethods?.[0]?.final;
  return typeof fallback === 'number' ? fallback : 0;
};

function handleUpdateUserInfoCallback({ success, retry, initiatedFrom }, closeUpdateUserInfo: () => void) {
  trackMpEvent({
    name: 'edit_profile_flow',
    properties: {
      step: success ? 'success' : 'error',
      initiated_from: initiatedFrom,
      buy_bundle_session_id: buyBundleFlowTrackingSession.getId(),
      error_type: success ? undefined : 'backend_error',
    },
  });

  if (success) {
    closeUpdateUserInfo();
  }

  toast(
    ({ closeToast }) => (
      <Snackbar
        label={_s(`${baseTranslationKey}.editProfileModal.snackbar.label.${success ? 'success' : 'error'}`)}
        type={success ? 'success' : 'danger'}
        action={
          <button onClick={success ? closeToast : retry}>
            {_s(`${baseTranslationKey}.editProfileModal.snackbar.action.${success ? 'success' : 'error'}`)}
          </button>
        }
      />
    ),
    { autoClose: 2000 },
  );
}

const BundleCheckout = () => {
  const {
    selectedPaymentMethod,
    summary,
    missingActions,
    submitting,
    cofThreeDS,
    swish,
    session,
    bundleCheckoutAPI,
    onChangePaymentMethod,
    onDismissSwishQRCode,
  } = useBundleCheckout();
  const { storedPaymentMethods, manualSetPaymentMethods, paymentMethods, isBokadirektPay, platformFee } =
    usePaymentMethodsContext();
  const klarnaBrands = getValidKlarnaBrands(
    paymentMethods,
    getBundleSummaryFinalPrice(summary) + (platformFee?.enabled ? platformFee.amount : 0),
  );
  const [klarnaVariant, setKlarnaVariant] = useState<KlarnaVariant | undefined>();
  const history = useHistory();
  const location = useLocation();
  const screenName = getScreenNameFromPath(location.pathname);

  // experiment with radio buttons and popular badge
  const useHighlightedPaymentMethods =
    useGetAmplitudeExperimentVariant()('highlighted-payment-methods')?.value === 'variant-popular-badge-visible';

  // refs
  const selectPaymentMethodContainerRef = useRef<HTMLDivElement>(null);
  const confirmationContainerRef = useRef<HTMLDivElement>(null);
  const tooltipTriggerRef = useRef<HTMLButtonElement>(null);
  const tooltipContainerRef = useRef<HTMLDivElement>(null);
  const addedCardIds = useRef(new Set<string>());

  // local state
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  // selectors
  const user = useAppSelector((state) => state.users)?.user;
  const bundle = useAppSelector((state) => state.bundle);

  const trackedCheckout: PaymentMethodCheckout = {
    flow: 'bundle',
    company: { id: bundle.place.id, name: bundle.place.about.name },
  };
  const otherPlaces = bundle.bundle?.places ? bundle.bundle.places.filter((p) => p.id !== bundle?.place?.id) : [];
  const otherLocationsItemProps = getOtherBundleLocations(otherPlaces);

  // hooks
  const checkoutCTAPosition = useIntersectionObserver(confirmationContainerRef);
  const isCheckoutCTAInView = checkoutCTAPosition === 'inView';
  const { isMobileView } = useMobileView();
  const loginContext = useContext(LoginContext);
  const pinInputProps = usePinInput({
    fields: 6,
    validateCallback: loginContext.handleLogin,
  });
  const { onClose: onCheckoutModalClose, ...checkoutModalContext } = useCheckoutModal();
  const updateUserInfo = useUpdateUserInfo({
    callback: (props) =>
      handleUpdateUserInfoCallback(
        { retry: props.retry, success: props.success, initiatedFrom: screenName },
        handleCloseEditProfile,
      ),
    onValidationError: () => {
      trackMpEvent({
        name: 'edit_profile_flow',
        properties: {
          step: 'error',
          initiated_from: screenName,
          error_type: 'validation_error',
        },
      });
    },
  });

  const updateUserInfoProps = {
    ...updateUserInfo,
    handleSubmit: () => {
      trackMpEvent({
        name: 'edit_profile_flow',
        properties: {
          step: 'verification',
          initiated_from: screenName,
          buy_bundle_session_id: buyBundleFlowTrackingSession.getId(),
        },
      });
      return updateUserInfo.handleSubmit();
    },
  };

  const { register, errors } = useFormData();

  // local methods
  const handleCloseCheckoutModal = () => {
    onCheckoutModalClose();
  };
  const handleCloseEditProfile = () => {
    setShowEditProfile(false);

    if (!updateUserInfo.hasSavedOnce.current) {
      trackMpEvent({
        name: 'edit_profile_flow',
        properties: {
          step: 'cancel',
          initiated_from: screenName,
          buy_bundle_session_id: buyBundleFlowTrackingSession.getId(),
        },
      });
    }
  };

  const handleShowEditProfile = () => {
    setShowEditProfile(true);
    trackMpEvent({
      name: 'edit_profile_flow',
      properties: {
        step: 'initiated',
        initiated_from: screenName,
        buy_bundle_session_id: buyBundleFlowTrackingSession.getId(),
      },
    });
  };

  const handleCloseLoginModal = () => {
    loginTrackingSession.track({ step: 'back_to_email_submission', flow: loginContext.flow });
    loginContext.clearLoginState();
  };

  const handleLoginSuccess = () => {
    loginContext.clearLoginState();
  };

  const handleDismissSwishQRCode = () => {
    onDismissSwishQRCode();
  };

  const handleShowTooltip = () => {
    setTooltipVisible(true);
  };

  const handleHideTooltip = () => {
    setTooltipVisible(false);
  };

  const handleChangePaymentMethod = (method: SelectedPaymentMethod) => {
    if (method.type === CHECKOUT_PAYMENT_METHOD.NEW_COF) {
      if (!isBokadirektPay) {
        checkoutModalContext.onOpen('cof');
      } else {
        checkoutModalContext.onOpen('bokadirektAddCard');
        onChangePaymentMethod(method);
      }
      return;
    }
    onChangePaymentMethod(method);
    trackPaymentMethodChange(selectedPaymentMethod, method, trackedCheckout, addedCardIds.current);

    const buildSummaryProperties = getSummaryProperties({ summary, selectedPaymentMethod: method });
    buyBundleFlowTrackingSession.setProperties(buildSummaryProperties({ type: 'buy_bundle_flow', data: bundle }));

    onCheckoutModalClose();
  };

  const handleOnCardAdded = (card: PaymentCard) => {
    addedCardIds.current.add(card.id);
    handleChangePaymentMethod({
      type: CHECKOUT_PAYMENT_METHOD.STORED_COF,
      brand: card.brand,
      id: card.id,
      lastFour: card.lastFour,
    });
  };

  const handleOnRemoveCard = (card: PaymentCard) => {
    if (selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.STORED_COF && selectedPaymentMethod.id === card.id) {
      handleChangePaymentMethod({
        type: CHECKOUT_PAYMENT_METHOD.NONE,
      });
    }
  };

  const handleBeforeThreeDSRedirect = () => {
    const validatedAdyenPaymentMethod = selectedPaymentMethodSchema.safeParse(selectedPaymentMethod);

    if (validatedAdyenPaymentMethod.success === false) {
      Sentry.captureException(validatedAdyenPaymentMethod.error);
      return;
    }

    saveCofCheckoutStateToLocalStorage({
      isAddNewCard: true,
      selectedPaymentMethod: validatedAdyenPaymentMethod.data,
    });
  };

  const handleOnCheckoutTermsOpen = () => {
    buyBundleFlowTrackingSession.terms('initiated', 'T&C_bd');
  };

  function handlePageLeave() {
    if (requireLogin && loginTrackingSession.isActive()) {
      loginContext.onClose();
    }
  }

  const onCompleteAdyenCheckout = useOnCompleteAdyenCheckout({
    session,
    resourceId: session?.bundleTemplateId,
    resourceType: 'bundle',
    storedPaymentMethods,
    paymentMethods,
    manualSetPaymentMethods,
    onSubmitting: () => {},
    onCardAdded: handleOnCardAdded,
    onNoNewCardFound: () => {
      bundleCheckoutAPI().removeSession?.();
      onCheckoutModalClose();
    },
    onError: () => {
      bundleCheckoutAPI().removeSession?.();
      onCheckoutModalClose();
      toast(({ closeToast }) => (
        <Snackbar
          label={_s(`${baseTranslationKey}.threeDSecureRedirectError`)}
          type="danger"
          onClose={closeToast}
        />
      ));
    },
    trackingSessionId: buyBundleFlowTrackingSession.getId(),
  });

  // computed & mappings
  const requireLogin = !isSistaminuten() && !user;
  const showThreeDSecure = Boolean(cofThreeDS?.adyenActionJson);
  const checkoutDetailsProps = mapBundleStateToCheckoutDetailsProps(bundle);
  const showFloatingCTA = isCheckoutCTAInView !== undefined && Boolean(!isCheckoutCTAInView);
  const showSwishQrCode = Boolean(swish?.showQrCode);

  const isWellnessQualified = useMemo(() => {
    const service = bundle.place.services
      .flatMap((service) => service.services)
      .find((service) => service.id === bundle.bundle.service.mpId);

    return Boolean(service?.about?.settings?.healthCareCard);
  }, [bundle.place.services, bundle.bundle.service.mpId]);

  const checkoutTerms = getCheckoutTerms(
    'bundle',
    TermLinkIdentifier.PAYMENT,
    getBundleSubmitLabel({ paymentMethod: selectedPaymentMethod.type, isMobileView, isFloating: !isCheckoutCTAInView }),
  );

  // lifecycle
  useEffect(() => {
    if (user && loginContext.view === 'verifyOtp') {
      loginContext.updateView('verifySuccess');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loginContext.view]);

  useEffect(() => {
    sessionStorage.setItem('paymentCheckoutReturnUrl', `/bundles/checkout/${bundle.place.id}/${bundle.bundle.id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * listen to history changes to detect when user navigates back
   */
  useEffect(() => {
    const unlisten = history.listen((_location, action) => {
      if (action === 'POP') {
        handlePageLeave();
      }
    });
    return unlisten;
  }, [history]);

  return (
    <CheckoutViewLayout
      header={{
        leftSlot: {
          button: {
            item: {
              identifier: HeaderButtonIdentifier.Back,
              icon: 'back',
            },
            'aria-label': _t('abort'),
            onClick: () => history.go(-1),
          },
          text: _t('abort'),
        },
        middleSlot: {
          title: checkoutDetailsProps.placeName,
          subtitle: !user ? _t('logIn') : _t('confirm'),
        },
        rightSlot: !isMobileView && checkoutDetailsProps.logo ? { imgUrl: checkoutDetailsProps.logo } : undefined,
      }}
      wrapperClass={isMobileView ? 'bg-surface_l4' : 'bg-gradient'}>
      {submitting && <LoadingPlaceHolder className="fixed h-screen" />}
      <div className="bg-brown-50 lg:bg-transparent">
        <div className="lg:py-xxl lg:container">
          <div className="gap-xxl flex items-start">
            <div className="gap-lg flex grow-[9999] basis-[600px] flex-col">
              <CardWrapper className="space-y-xs pb-md w-full lg:hidden">
                <CheckoutDetails {...checkoutDetailsProps} />
                {otherLocationsItemProps?.length > 0 && (
                  <div className="px-lg">
                    <div className="mt-md pt-sm border-black-100 border-t">
                      <Header label={_s(`${baseTranslationKey}.otherLocations.title`)} size="md" />

                      {otherLocationsItemProps.map((otherLocation) => (
                        <BundleOtherLocationsListItem key={otherLocation.id} {...otherLocation} />
                      ))}
                    </div>
                  </div>
                )}
                <div className="px-lg">
                  <BundleNoticeBanner />
                </div>
              </CardWrapper>
              {requireLogin && (
                <CardWrapper>
                  <UserLogin {...loginContext} initiatedFrom="checkout_prior_bundle_summary" />
                </CardWrapper>
              )}
              {!requireLogin && (
                <>
                  <CardWrapper>
                    <UserInfo
                      email={user?.contact?.email ?? ''}
                      name={`${user?.about?.givenName ?? ''} ${user?.about?.familyName ?? ''}`}
                      phone={user?.contact?.phone ?? ''}
                      initials={getUserInitials(user)}
                      onClick={handleShowEditProfile}
                    />
                  </CardWrapper>
                  <div id="tooltip-slot" className="relative" ref={tooltipContainerRef} />
                  {tooltipVisible && (
                    <TooltipPortal
                      onClose={handleHideTooltip}
                      triggerRef={tooltipTriggerRef}
                      targetRef={tooltipContainerRef}
                      onClickOutside={handleHideTooltip}
                      offsetTop={44}
                      offsetLeft={-24}>
                      <div className="p-lg space-y-md">
                        <div className="flex items-start justify-between">
                          <span className="text-md max-w-[200px] font-bold text-white" id="popover-title">
                            {_s(`${baseTranslationKey}.formfields.nationalId.tooltip.title`)}
                          </span>
                          <button onClick={handleHideTooltip} aria-label={_s('close')}>
                            <Icon variant="close-alt" color="white" />
                          </button>
                        </div>
                        <p className="text-black-300 text-sm" id="popover-content">
                          {_s(`${baseTranslationKey}.formfields.nationalId.tooltip.message`)}
                        </p>
                      </div>
                    </TooltipPortal>
                  )}
                  {isWellnessQualified && (
                    <Fragment>
                      <CardWrapper className="pb-lg">
                        <div className="pl-lg py-md">
                          <Header label={_s(`${baseTranslationKey}.formfields.header`)} size="md" />
                        </div>
                        <div className="flex flex-col">
                          <ListInput
                            id="nationalId"
                            label={_s(`${baseTranslationKey}.formfields.nationalId.label`)}
                            placeholder={_s(`${baseTranslationKey}.formfields.nationalId.placeholder`)}
                            floatStyle
                            {...(Boolean(errors.nationalId?.message)
                              ? {
                                  error: errors.nationalId?.message,
                                }
                              : {
                                  rightIcon: (
                                    <button
                                      className="focus-visible:outline-black-900 rounded-sm focus:outline-none focus-visible:outline"
                                      ref={tooltipTriggerRef}
                                      onClick={handleShowTooltip}>
                                      <Icon variant="info-circle" color="information-700" />
                                    </button>
                                  ),
                                })}
                            {...register('nationalId', {
                              validate: (nationalId) => {
                                if (nationalId.trim() === '') return true;
                                return !isNationalId(nationalId.trim()) ? _s('invalidNationalId') : true;
                              },
                            })}
                          />
                        </div>
                      </CardWrapper>
                    </Fragment>
                  )}

                  <CardWrapper>
                    <div ref={selectPaymentMethodContainerRef}>
                      <PromotedPaymentMethods
                        summary={summary}
                        selectedPaymentMethod={selectedPaymentMethod}
                        onChangePaymentMethod={handleChangePaymentMethod}
                        triggerSource={SCREEN_NAME.CHECKOUT_BUNDLE_SUMMARY}
                        useHighlighted={useHighlightedPaymentMethods}
                        error={missingActions.includes('payment-method')}
                        {...checkoutModalContext}
                      />
                    </div>
                  </CardWrapper>

                  <div ref={confirmationContainerRef}>
                    <CardWrapper>
                      <div className="gap-xl p-lg flex flex-col">
                        <SubmitCTA />
                        <CheckoutTerms
                          description={checkoutTerms.description}
                          linkLabel={checkoutTerms.linkLabel}
                          termsUrl={checkoutTerms.termsUrl}
                          onTermsOpen={handleOnCheckoutTermsOpen}
                        />
                      </div>
                    </CardWrapper>
                  </div>
                  <FloatingCTAWrapper
                    description={checkoutTerms.description}
                    linkLabel={checkoutTerms.linkLabel}
                    termsUrl={checkoutTerms.termsUrl}
                    show={showFloatingCTA}
                    onTermsOpen={handleOnCheckoutTermsOpen}>
                    <SubmitCTA floating />
                  </FloatingCTAWrapper>
                </>
              )}
            </div>
            <div className="sticky bottom-20 top-20 hidden lg:flex lg:grow-[100] lg:basis-[390px]">
              <CardWrapper className="space-y-lg w-full">
                <CheckoutDetails key={2} {...checkoutDetailsProps} />
                {otherLocationsItemProps?.length > 0 && (
                  <div className="px-lg">
                    <div className="mt-md pt-sm border-black-100 border-t">
                      <Header label={_s(`${baseTranslationKey}.otherLocations.title`)} size="md" />

                      {otherLocationsItemProps.map((otherLocation) => (
                        <BundleOtherLocationsListItem key={otherLocation.id} {...otherLocation} />
                      ))}
                    </div>
                  </div>
                )}
                <div className="px-lg">
                  <BundleNoticeBanner />
                </div>
              </CardWrapper>
            </div>
          </div>
        </div>
      </div>
      {checkoutModalContext.screen === 'klarnaOptions' && (
        <KlarnaOptionsModal
          isOpen={checkoutModalContext.isOpen}
          onClose={onCheckoutModalClose}
          brands={klarnaBrands}
          onSelect={async (variant) => {
            setKlarnaVariant(variant);
            checkoutModalContext.onOpen('bokadirektPay');
            const success = await bundleCheckoutAPI().submitAdyen?.();
            if (!success) onCheckoutModalClose();
          }}
        />
      )}
      {checkoutModalContext.screen === 'walleyPhoneInput' && (
        <WalleyPhoneModal
          isOpen={checkoutModalContext.isOpen}
          onClose={onCheckoutModalClose}
          onSubmit={async (phone) => {
            checkoutModalContext.onOpen('bokadirektPay');
            const success = await bundleCheckoutAPI().submitAdyen?.({ walleyPhone: phone });
            if (!success) onCheckoutModalClose();
          }}
        />
      )}
      {(checkoutModalContext.screen === 'bokadirektPay' || checkoutModalContext.screen === 'bokadirektAddCard') && (
        <AdyenCheckoutComponent
          session={session}
          onClose={() => {
            bundleCheckoutAPI().removeSession?.();
            onCheckoutModalClose();
          }}
          isOpen={checkoutModalContext.isOpen}
          submitting={submitting}
          onComplete={onCompleteAdyenCheckout}
          amount={selectedPaymentMethod.final}
          paymentMethodType={
            checkoutModalContext.screen === 'bokadirektAddCard'
              ? CHECKOUT_PAYMENT_METHOD.NEW_COF
              : selectedPaymentMethod.type
          }
          klarnaVariant={checkoutModalContext.screen === 'bokadirektPay' ? klarnaVariant : undefined}
        />
      )}
      {checkoutModalContext.screen === 'cof' && (
        <CreditCardModal
          isOpen={checkoutModalContext.isOpen}
          onClose={handleCloseCheckoutModal}
          onCardAdded={handleOnCardAdded}
          onBeforeThreeDSRedirect={handleBeforeThreeDSRedirect}
          returnPath={`/bundles/checkout/${bundle.place?.id}/${bundle.bundle?.id}`}
          traceTrackingSession={{ buy_bundle_session_id: buyBundleFlowTrackingSession.getId() }}
          {...checkoutModalContext}
        />
      )}
      {checkoutModalContext.screen !== 'cof' &&
        checkoutModalContext.screen !== 'bokadirektPay' &&
        checkoutModalContext.screen !== 'bokadirektAddCard' &&
        checkoutModalContext.screen !== 'klarnaOptions' &&
        checkoutModalContext.screen !== 'walleyPhoneInput' && (
          <CheckoutModal
            selectedPaymentMethod={selectedPaymentMethod}
            onChangePaymentMethod={handleChangePaymentMethod}
            onApplyGiftcard={() => null}
            onClose={handleCloseCheckoutModal}
            {...checkoutModalContext}
          />
        )}
      {showThreeDSecure && (
        <Modal isOpen={!submitting}>
          <Modal.Content floating={!isMobileView}>
            <div className={classnames('gap-xl pb-lg px-lg flex flex-col', isMobileView && 'px-lg')}>
              <ThreeDSComponent
                action={JSON.parse(cofThreeDS?.adyenActionJson)}
                handleOnAdditionalDetails={(data) => bundleCheckoutAPI().submitThreeDS(data)}
              />
            </div>
          </Modal.Content>
        </Modal>
      )}
      {showSwishQrCode && <SwishModal onClose={handleDismissSwishQRCode} qrCode={swish.payment.qrCode} />}
      {(loginContext.view === 'verifyOtp' || loginContext.view === 'verifySuccess') && (
        <Modal isOpen>
          <Modal.Content floating={!isMobileView}>
            <Modal.Header
              title={_s(`loginModal.verifyOtp.title`)}
              {...(loginContext.view === 'verifyOtp' && { onClose: handleCloseLoginModal })}
            />
            <div className={classnames('gap-xl pb-lg px-lg flex flex-col', isMobileView && 'px-lg')}>
              {loginContext.view === 'verifyOtp' && <VerifyOTP {...loginContext} {...pinInputProps} />}
              {loginContext.view === 'verifySuccess' && (
                <VerifySuccess email={loginContext.email} onAnimationComplete={handleLoginSuccess} />
              )}
            </div>
          </Modal.Content>
        </Modal>
      )}
      <Modal isOpen={showEditProfile} onRequestClose={handleCloseEditProfile}>
        <Modal.Content floating={!isMobileView}>
          <Modal.Header title={_s(`${baseTranslationKey}.editProfileModal.title`)} onClose={handleCloseEditProfile} />
          <div className={classnames(isMobileView && 'px-lg')}>
            <UpdateUserInfo
              {...updateUserInfoProps}
              givenName
              familyName
              dob
              gender
              locality
              postalCode
              streetAddress
              mobile
            />
          </div>
        </Modal.Content>
      </Modal>
      <SEO
        {...seoPropsFromBaseString('checkoutBundle', `${url.getBaseUrl()}bundles/checkout`)}
        noindex={isSistaminuten()}
        image={url.getBaseImageUrl()}
      />
    </CheckoutViewLayout>
  );
};

const SubmitCTA = ({ floating = false }: { floating?: boolean }) => {
  const user = useAppSelector((state) => state?.users?.user);
  const { selectedPaymentMethod, summary, submitting, missingActions, onCheckoutMissingAction, bundleCheckoutAPI } =
    useBundleCheckout();
  const {
    isBokadirektPay,
    paymentMethods: submitPaymentMethods,
    platformFee: submitPlatformFee,
  } = usePaymentMethodsContext();
  const checkoutModalContext = useCheckoutModal();
  const { isMobileView } = useMobileView();
  const { handleSubmit } = useFormData();
  const paymentMethod = selectedPaymentMethod.type;
  const { final } = summary.availablePaymentMethods.find((method) => method.type === paymentMethod);
  const invalidPaymentMethod = paymentMethod === CHECKOUT_PAYMENT_METHOD.NONE;
  const klarnaBrands = getValidKlarnaBrands(
    submitPaymentMethods,
    getBundleSummaryFinalPrice(summary) + (submitPlatformFee?.enabled ? submitPlatformFee.amount : 0),
  );

  const handleOnClick = async () => {
    buyBundleFlowTrackingSession.bookingCTAClicked();

    const missingActions: CheckoutMissingAction[] = [];
    if (invalidPaymentMethod) missingActions.push('payment-method');

    missingActions.forEach((action) => onCheckoutMissingAction(action));

    if (missingActions.length) {
      buyBundleFlowTrackingSession.validationError(
        (() => {
          let errors = [];
          if (missingActions.includes('participants')) errors.push('missing_participants');
          if (missingActions.includes('practitioner-terms')) errors.push('merchant_terms');
          if (missingActions.includes('payment-method')) errors.push('payment_methods');
          if (missingActions.includes('email')) errors.push('email_missing');
          return errors;
        })(),
      );
      return;
    }

    if (isBokadirektPay) {
      if (paymentMethod === CHECKOUT_PAYMENT_METHOD.KLARNA) {
        if (klarnaBrands.length === 1) {
          checkoutModalContext.onOpen('bokadirektPay');
          const success = await bundleCheckoutAPI().submitAdyen?.();
          if (!success) checkoutModalContext.onClose();
          return;
        }
        return checkoutModalContext.onOpen('klarnaOptions');
      }
      if (paymentMethod === CHECKOUT_PAYMENT_METHOD.WALLEY) {
        const userPhone = user?.contact?.mobile || user?.contact?.phone;
        const hasValidUserPhone = userPhone ? validatePhoneNumber(userPhone) : false;
        if (!hasValidUserPhone) {
          return checkoutModalContext.onOpen('walleyPhoneInput');
        }
        checkoutModalContext.onOpen('bokadirektPay');
        const success = await bundleCheckoutAPI().submitAdyen?.({ walleyPhone: userPhone });
        if (!success) checkoutModalContext.onClose();
        return;
      }
      if (paymentMethod !== CHECKOUT_PAYMENT_METHOD.STORED_COF) {
        checkoutModalContext.onOpen('bokadirektPay');
      }
      const success = await bundleCheckoutAPI().submitAdyen?.();
      if (!success) checkoutModalContext.onClose();
      return;
    }

    switch (paymentMethod) {
      case CHECKOUT_PAYMENT_METHOD.STORED_COF:
        return bundleCheckoutAPI().submitCoF(selectedPaymentMethod.id);
      case CHECKOUT_PAYMENT_METHOD.QLIRO:
        return bundleCheckoutAPI().initQliro();
      case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
        return bundleCheckoutAPI().submitGooglePay(final);
      case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
        return bundleCheckoutAPI().submitApplePay(final);
      case CHECKOUT_PAYMENT_METHOD.SWISH:
        return bundleCheckoutAPI().submitSwish();
    }
  };

  const ctaIconVariant = getCheckoutSubmitIconVariantIfAny(selectedPaymentMethod);

  const buttonProps: ButtonProps = {
    size: floating ? 'sm' : 'lg',
    block: !floating,
    disabled: submitting || Boolean(missingActions.length),
    label: getBundleSubmitLabel({ paymentMethod, isFloating: floating, isMobileView }),
    onClick: handleSubmit(handleOnClick),
    leftIcon: ctaIconVariant ? <Icon variant={ctaIconVariant} noFilter /> : null,
    iconNoFilter: Boolean(ctaIconVariant),
  };

  return <Button {...buttonProps} />;
};

const BundleCheckoutInnerProviders: React.FC<{
  cards: React.ComponentProps<typeof BundleCheckoutProvider>['cards'];
  paymentHistory: React.ComponentProps<typeof BundleCheckoutProvider>['paymentHistory'];
}> = ({ cards, paymentHistory }) => {
  const { isBokadirektPay } = usePaymentMethodsContext();
  const user = useAppSelector((state) => state?.users?.user);
  // The cards endpoint authenticates off the session token, which outlives a late or failed profile fetch.
  // The profile selector stays so a mid-checkout login re-renders this.
  const isLoggedIn = !!user || !!loggedUser();
  return (
    <UseCardsProvider initialCards={cards} fetchOnMount={!isBokadirektPay && isLoggedIn}>
      <FormDataProvider>
        <BundleCheckoutProvider cards={cards} paymentHistory={paymentHistory}>
          <CheckoutModalProvider>
            <BundleCheckout />
          </CheckoutModalProvider>
        </BundleCheckoutProvider>
      </FormDataProvider>
    </UseCardsProvider>
  );
};

export default withBundleCheckoutFallback(
  withWalletPayment(({ isLoading: checkWalletMethodsLoading }) => {
    const location = useLocation();
    const history = useHistory();
    const user = useAppSelector((state) => state?.users?.user);
    const bundleState = useAppSelector((state) => state.bundle);
    const parsedBundleState = bundleStateSchema.safeParse(bundleState);

    if (parsedBundleState.success === false) {
      buyBundleFlowTrackingSession.error(['summary_error']);
      return <GoBack />;
    }

    const isLoggedIn = !!user || !!loggedUser();
    const { cards, loading, error } = useCardsManager([], isLoggedIn);

    const isLoadingCards = loading && !error && !cards.length;

    const { paymentHistory, loading: loadingPaymentHistory } = usePaymentsHistory(bundleState.place.id);

    useEffect(() => {
      const threeDSecureRedirectResultSuccess = getThreeDSecureRedirectResultIfAny(location, history);
      if (threeDSecureRedirectResultSuccess || threeDSecureRedirectResultSuccess === undefined) return;
      toast(({ closeToast }) => (
        <Snackbar label={_s(`${baseTranslationKey}.threeDSecureRedirectError`)} type="danger" onClose={closeToast} />
      ));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (checkWalletMethodsLoading || isLoadingCards || loadingPaymentHistory) return <LoadingPlaceHolder />;

    const organisationNumber = bundleState.bundle?.organisationNumber ?? null;
    const placeId = bundleState.place?.id ?? null;

    return (
      <UsePaymentMethodsProvider
        placeId={placeId}
        employeeId={null}
        organisationNumber={organisationNumber}
        fetchOnMount={!!organisationNumber}>
        <WithPaymentMethodsLoaded>
          <BundleCheckoutInnerProviders cards={cards} paymentHistory={paymentHistory} />
        </WithPaymentMethodsLoaded>
      </UsePaymentMethodsProvider>
    );
  }),
);
